import React, { useState, useRef, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, Button, Spinner } from "reactstrap";
import PropTypes from "prop-types";
import CreatableSelect from "react-select/creatable";
import "./ModalAssignTags.scss";
import { useTranslation } from "react-i18next";
import useGetAllTags from "src/Hooks/TagFunction/useGetTags";
import useUpdateResourceTag from "src/Hooks/TagFunction/useUpdateResourceTag";
import isEmpty from "lodash/isEmpty";
import { useSetRecoilState } from "recoil";
import { alertState } from "src/recoil/atoms/global";
import useGetTagsByResources from "src/Hooks/TagFunction/useGetTagsByResources";
import useDeleteResourceTag from "src/Hooks/TagFunction/useDeleteResourcetag";
import useTagValidation from "src/Hooks/TagFunction/useTagValidation";
import truncate from "lodash/truncate";
import classNames from "classnames";
import { scrollToLatestTag } from "src/utils/common";

const ModalAssignTags = ({ isOpen, onClose, resourceType, resourcesSelected }) => {
  const { t } = useTranslation("common");
  const showAlert = useSetRecoilState(alertState);
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const selectRef = useRef(null);

  const {
    data: allTagsData,
    isFetching: isLoadingTags,
    refetch: refetchAllTags,
  } = useGetAllTags({
    enabled: isOpen && !isEmpty(resourcesSelected),
    refetchOnWindowFocus: false,
  });

  const {
    data: resourceTagsData,
    isFetching: isLoadingTagsByResources,
    refetch: refetchResourceTags,
  } = useGetTagsByResources({
    resourceIds: resourcesSelected,
    resourceType,
    enabled: isOpen && !isEmpty(resourcesSelected),
    refetchOnWindowFocus: false,
  });

  const { mutate: updateResourceTagMutate, isLoading: isUpdatingTags } = useUpdateResourceTag({
    showAlert,
    t,
    onSuccess: () => {
      if (selectRef.current) {
        selectRef.current.blur();
      }
      setInputValue("");
      setErrorMessage("");
      setSelectedTags([]);
      refetchAllTags();
      refetchResourceTags();
    },
  });

  const { mutate: deleteResourceTagMutate, isLoading: isDeletingTag } = useDeleteResourceTag({
    showAlert,
    t,
    onSuccess: () => {
      setInputValue("");
      setErrorMessage("");
      refetchAllTags();
      refetchResourceTags();
    },
  });

  const getSuggestionOptions = (allTagsData, resourceTagsData) => {
    if (isLoadingTags || isLoadingTagsByResources) return [];

    return (
      allTagsData?.items
        ?.filter((suggestion) => {
          const isInResourceTags = resourceTagsData?.some(
            (resourceTag) => resourceTag.name.toLowerCase() === suggestion.name.toLowerCase()
          );

          return !isInResourceTags;
        })
        .map((suggestion) => ({
          value: suggestion.id,
          label: suggestion.name,
        })) || []
    );
  };

  const suggestionOptions = getSuggestionOptions(allTagsData, resourceTagsData);

  const { errorMessage, setErrorMessage, prepareTags, clearError } = useTagValidation(tags, allTagsData);

  const validateAndFilterOptions = (selectedOptions, existingTags) => {
    const uniqueOptions = [];
    const seenTagsName = new Set();
    let hasDuplicate = false;
    let hasLengthError = false;
    let errorTagName = "";

    for (const option of selectedOptions) {
      const tagNameLowerCase = option.label.toLowerCase();

      if (option.label.length > 255) {
        hasLengthError = true;
        errorTagName = option.label;
        continue;
      }

      if (seenTagsName.has(tagNameLowerCase)) {
        hasDuplicate = true;
        errorTagName = option.label;
        continue;
      }

      const existsInCurrentTags = existingTags.some((tag) => tag.name.toLowerCase() === tagNameLowerCase);
      if (existsInCurrentTags) {
        hasDuplicate = true;
        errorTagName = option.label;
        continue;
      }

      seenTagsName.add(tagNameLowerCase);
      uniqueOptions.push(option);
    }

    return {
      uniqueOptions,
      hasDuplicate,
      hasLengthError,
      errorTagName,
    };
  };

  const submitTags = (tagsToAdd) => {
    const updatedTags = [...tags, ...tagsToAdd];
    const dataSubmit = {
      resource_type: resourceType,
      item_ids: resourcesSelected,
      tags: updatedTags,
    };

    updateResourceTagMutate({ data: dataSubmit });
    setInputValue("");
  };

  const handleTagChange = (selectedOptions) => {
    if (isEmpty(selectedOptions)) {
      setSelectedTags([]);
      clearError();
      setInputValue("");
      return;
    }

    const trimmedOptions = selectedOptions
      .map((option) => ({
        ...option,
        value: option.__isNew__ ? option.value.trim() : option.value,
        label: option.__isNew__ ? option.label.trim() : option.label,
      }))
      .filter((option) => {
        if (option.__isNew__ && (!option.label || option.label.trim() === "")) {
          return false;
        }
        return true;
      });

    const { uniqueOptions, hasDuplicate, hasLengthError, errorTagName } = validateAndFilterOptions(trimmedOptions, tags);

    if (hasLengthError) {
      setErrorMessage(t("max_length", { fieldName: `"${truncate(errorTagName, { length: 20 })}"`, maxLength: 255 }));
      if (selectRef.current) {
        selectRef.current.blur();
        return;
      }
      return;
    }
    if (hasDuplicate) {
      setErrorMessage(t("field_already_exists", { fieldName: `"${truncate(errorTagName, { length: 20 })}"` }));
      if (selectRef.current) {
        selectRef.current.blur();
        return;
      }
      return;
    }

    setSelectedTags(uniqueOptions);
    clearError();
    setInputValue("");
    scrollToLatestTag(selectRef, ".tag-modal-container__value-container", ".tag-modal-container__multi-value");
  };

  const handleAddTags = () => {
    const tagsToAdd = prepareTags(inputValue, selectedTags);

    if (tagsToAdd === null) {
      // Validation failed, error message already set
      return;
    }

    if (tagsToAdd.length > 0) {
      submitTags(tagsToAdd);
    }
  };

  const handleDeleteTag = (tag) => {
    const item_ids = Array.isArray(resourcesSelected) ? resourcesSelected.join(",") : resourcesSelected;

    const params = {
      resource_type: resourceType,
      item_ids,
      tag_id: tag?.id?.toString(),
    };

    deleteResourceTagMutate(params);
  };

  const handleClose = () => {
    setSelectedTags([]);
    clearError();
    setInputValue("");
    onClose();
  };

  const customStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: "38px",
      maxHeight: "76px", // 2 rows height - include padding y
      border: "1px solid #ced4da",
      boxShadow: "none",
      "&:hover": {
        border: "1px solid #adb5bd",
      },
    }),
    valueContainer: (base) => ({
      ...base,
      maxHeight: "72px", // exclude padding y
      overflowY: "auto",
      paddingTop: "2px",
      paddingBottom: "2px",
    }),
    multiValue: (base) => ({
      ...base,
      margin: "2px",
    }),
    singleValue: (base) => ({
      ...base,
      fontSize: "14px",
    }),
  };

  useEffect(() => {
    if (isOpen) {
      setTags(resourceTagsData || []);
    }
  }, [isOpen, resourceTagsData]);

  return (
    <Modal isOpen={isOpen} toggle={!isDeletingTag ? handleClose : undefined} className="modal-assign-tag" size="lg" backdrop="static">
      <ModalHeader
        close={
          <button
            type="button"
            className={classNames("btn-close", {
              "opacity-50 not-allowed": isUpdatingTags || isDeletingTag,
              "opacity-100 pointer": !isUpdatingTags && !isDeletingTag,
            })}
            onClick={handleClose}
            disabled={isUpdatingTags || isDeletingTag}
          />
        }
        className="d-flex align-items-center p-a-16"
      >
        {resourcesSelected.length > 1
          ? t("assign_tags_title_for_multi_simulation")
          : t("assign_tags_title_for_single_simulation", { simulationId: `#${resourcesSelected?.[0]}` })}
      </ModalHeader>
      <ModalBody className="p-b-24">
        <div className="row">
          <div className="col-1" />
          <div className="col-10">
            <div className="tag-input-container">
              <div className="d-flex justify-content-center align-items-center gap-2 fit-content">
                <CreatableSelect
                  ref={selectRef}
                  isMulti
                  isClearable
                  value={selectedTags}
                  inputValue={inputValue}
                  options={suggestionOptions}
                  onChange={handleTagChange}
                  onInputChange={(value, action) => {
                    if (action.action === "input-change") {
                      setInputValue(value);
                    }
                  }}
                  blurInputOnSelect={false}
                  closeMenuOnSelect={false}
                  placeholder={t("select_tags_placeholder")}
                  formatCreateLabel={(inputValue) => `${t("create_tag_dropdown")} "${inputValue.trim()}"`}
                  noOptionsMessage={() => t("no_tags_found")}
                  className="react-select-container"
                  classNamePrefix="tag-modal-container"
                  isValidNewOption={(inputValue) => inputValue && inputValue.trim().length > 0}
                  isLoading={isLoadingTags}
                  isDisabled={isLoadingTags || isUpdatingTags || isDeletingTag}
                  styles={customStyles}
                />
                <button
                  type="button"
                  className="btn btn-sm btn-primary min-width-by-px-120 width-by-px-120"
                  onClick={handleAddTags}
                  disabled={(isEmpty(inputValue.trim()) && selectedTags.length === 0) || isUpdatingTags || isLoadingTags || isDeletingTag}
                >
                  {t("add_tags_btn")} <i className="bi bi-plus f-s-14 text-white" />
                </button>
              </div>
              {errorMessage && <div className="position-absolute text-danger small">{errorMessage}</div>}
            </div>
          </div>
          <div className="col-1" />
        </div>

        {isLoadingTagsByResources && isOpen ? (
          <div className="row mt-3">
            <div className="col-12">
              <div className="tags-list">
                <div className="tags-container height-by-px-35 d-flex justify-content-center align-items-center">
                  <Spinner size="sm" className="me-2" />
                  <span className="text-muted">{t("loading")}...</span>
                </div>
              </div>
            </div>
          </div>
        ) : tags.length > 0 ? (
          <div className="row mt-3">
            <div className="col-12">
              <div className="tags-list">
                <div className="tags-container max-height-by-px-200 d-flex flex-wrap gap-1 p-r-4">
                  {tags.map((tag) => (
                    <div key={tag.id || tag.name} className="tag-item d-flex align-items-center justify-content-between fs-14 p-x-12 p-y-6">
                      <div className="tag-content d-flex align-items-center flex-fill">
                        <span className="tag-name text-break">{tag.name}</span>
                      </div>
                      <Button
                        color="link"
                        size="sm"
                        className="delete-tag-btn p-0 btn btn-link btn-sm m-l-8 border-0 d-flex justify-content-center align-items-center flex-shrink-0 width-by-px-20 height-by-px-20 rounded-circle"
                        onClick={() => handleDeleteTag(tag)}
                        disabled={isUpdatingTags || isDeletingTag}
                      >
                        <i className="bi bi-x f-s-14 text-dark" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="row mt-3">
            <div className="col-12">
              <div className="tags-list">
                <div className="tags-container height-by-px-35 d-flex justify-content-center align-items-center">
                  <span className="text-muted">{t("no_tags_found")}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

ModalAssignTags.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  resourceType: PropTypes.string.isRequired,
  resourcesSelected: PropTypes.array.isRequired,
  onSuccess: PropTypes.func,
};

export default ModalAssignTags;
